// StatusPage.tsx
import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import PageHeader from 'src/components/organisms/PageHeader';
import Footer from 'src/components/molecules/footer/Footer';
import ContentCardsContainer, {
  ContentCard,
} from 'src/components/organisms/ContentCard';
import { GridColDef } from '@mui/x-data-grid-premium';
import {
  tableSettingsProps,
  useTableSettings,
} from 'src/hooks/table/tableSettings/useTableSettings';
import { CustomDataGrid } from 'src/components/organisms/CustomDataGrid';
import menuItems from 'src/components/organisms/sidebarMenu/items';
import { pagesRoutes } from 'src/routes';

/* ------------------------------------------------------------------ */
/*  ⚙️  QUICK CONFIG                                                  */
/* ------------------------------------------------------------------ */
const PRODUCTS_ONLY = false; // ← flip to true to check only the “Products” route
const POLL_INTERVAL_MS = 60_000; // 1 min
const SECOND_PASS_DELAY_MS = 20_000;
const MAX_PARALLEL_IFRAMES  = 5;  

/* ------------------------------------------------------------------ */
/*  Types                                                             */
/* ------------------------------------------------------------------ */
interface StatusRow {
  id: string;
  name: string;
  path: string;
  isOk: boolean | null; // null = Loading/Unknown
  lastChecked?: string;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */
/** Replace one element in an array immutably */
const replaceRow = (rows: StatusRow[], updated: StatusRow): StatusRow[] => {
  const idx = rows.findIndex((r) => r.id === updated.id);
  if (idx === -1) return rows;
  const clone = [...rows];
  clone[idx] = updated;
  return clone;
};

/** Build an unseen iframe, wait a tick, then read its body text. */
const iframeCheck = (
  url: string,
  firstTimeout = 3_000,
  followUpDelay = SECOND_PASS_DELAY_MS,
): Promise<boolean> =>
  new Promise((resolve) => {
    /* ---------- build hidden, sandboxed frame ---------- */
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.setAttribute('sandbox', 'allow-same-origin allow-scripts'); // ⛔ no parent access
    const bust = `__statusBust=${Date.now().toString(36)}`;
    iframe.src = url + (url.includes('?') ? '&' : '?') + bust;

    let secondLookTimer: number | null = null;

    const cleanup = (ok: boolean) => {
      if (secondLookTimer) clearTimeout(secondLookTimer);
      iframe.onload = iframe.onerror = null;
      try {
        iframe.src = 'about:blank';   // stop scripts right now
        iframe.remove();
      } catch {/* ignore */}
      resolve(ok);
    };

    /* fallback: load never fires */
    const loadGuard = window.setTimeout(() => cleanup(false), firstTimeout);

    iframe.onload = () => {
      clearTimeout(loadGuard);

      const hadError = (): boolean => {
        try {
          return (
            iframe.contentDocument?.body?.innerText
              ?.toLowerCase()
              .includes('something went wrong') ?? true
          );
        } catch {
          return true; // cross-origin or any access issue → treat as failure
        }
      };

      /* 1️⃣ first peek */
      if (hadError()) {
        cleanup(false);
        return;
      }

      /* 2️⃣ second peek after delay */
      secondLookTimer = window.setTimeout(
        () => cleanup(!hadError()),
        followUpDelay,
      );
    };

    iframe.onerror = () => {
      clearTimeout(loadGuard);
      cleanup(false);
    };

    document.body.appendChild(iframe);
  });

/* ------------------------------------------------------------------ */
/*  useMenuPages – flatten menu → rows                                */
/* ------------------------------------------------------------------ */
const useMenuPages = (): StatusRow[] => {
  /** Recursively walk the menu tree */
  const traverse = (items: any[], acc: StatusRow[] = []): StatusRow[] => {
    items.forEach((item) => {
      const path = item.link as string | undefined;

      if (
        path &&
        path !== '' &&
        !path.startsWith('testing') &&
        !path.startsWith('/testing')
      ) {
        const shouldInclude =
          !PRODUCTS_ONLY ||
          path === '/products/products' || // “Products” landing
          path.startsWith('/products/'); // or any product-subpage

        if (
          shouldInclude &&
          !acc.some((row) => row.path === path)
        ) {
          acc.push({
            id: path,
            name: item.name ?? path,
            path,
            isOk: null, // still loading
          });
        }
      }

      if (item.items?.length) traverse(item.items, acc);
    });
    return acc;
  };

  return useMemo(() => traverse(menuItems), []);
};

/* ------------------------------------------------------------------ */
/*  usePageStatuses – checks pages one by one, updates immediately    */
/* ------------------------------------------------------------------ */
const usePageStatuses = (
  initialRows: StatusRow[],
  intervalMs = POLL_INTERVAL_MS,
): StatusRow[] => {
  const [rows, setRows] = useState<StatusRow[]>(initialRows);
  const rowsRef = useRef(rows);              // live snapshot
  const refreshing = useRef(false);          // “sweep in progress?”
  const timerRef   = useRef<number>();       // next sweep handle

  useEffect(() => { rowsRef.current = rows; }, [rows]);

  /* ─────────────────────────────── helpers ─────────────────────────────── */
  const checkPage = useCallback(async (row: StatusRow): Promise<StatusRow> => {
    try {
      /* 1️⃣ quick fetch (fast fail) */
      const res = await fetch(row.path, { method: 'GET' });
      const txt = await res.text();
      if (txt.toLowerCase().includes('something went wrong')) {
        return { ...row, isOk: false, lastChecked: new Date().toISOString() };
      }

      /* 2️⃣ secondary iframe check (handles late JS errors) */
      const ok = await iframeCheck(row.path);
      return { ...row, isOk: ok, lastChecked: new Date().toISOString() };
    } catch {
      return { ...row, isOk: false, lastChecked: new Date().toISOString() };
    }
  }, []);

  /** One full table sweep, limited to MAX_PARALLEL_IFRAMES workers. */
  const runSweep = useCallback(async () => {
    if (refreshing.current) return;          // overlap guard
    refreshing.current = true;

    let cursor = 0;
    const pool = Math.min(MAX_PARALLEL_IFRAMES, rowsRef.current.length);

    const worker = async () => {
      while (cursor < rowsRef.current.length) {
        const row = rowsRef.current[cursor++];
        const updated = await checkPage(row);
        setRows((prev) => replaceRow(prev, updated));
      }
    };

    await Promise.all(Array.from({ length: pool }, worker));
    refreshing.current = false;
    timerRef.current = window.setTimeout(runSweep, intervalMs);
  }, [checkPage, intervalMs]);

  /* kick-off once, clear timer on unmount */
  useEffect(() => {
    runSweep();                                 // first run immediately
    return () => { clearTimeout(timerRef.current); };
  }, [runSweep]);

  return rows;
};

const useRoutePages = (): StatusRow[] => {
  type Route = { path?: string; element?: React.ReactNode; children?: Route[] };

  /** Return true if the accumulated path contains “/testing” or “/templates”. */
  const isExcluded = (full: string) =>
    /(^|\/)(testing|templates)(\/|$)/.test(full);

  const traverse = (
    routes: Route[],
    parent: string,
    seen: Map<React.ReactNode, string>,
    acc: StatusRow[],
  ) => {
    routes.forEach((r) => {
      const seg  = r.path ?? '';
      const full =
        seg === '' ? parent : `${parent}/${seg}`.replace(/\/+/g, '/');

      /* 🚫 skip this branch entirely */
      if (isExcluded(full)) return;

      const isLeaf = !!r.element;

      if (isLeaf) {
        const include =
          !PRODUCTS_ONLY ||
          full === '/products/products' ||
          full.startsWith('/products/');

        if (include && !seen.has(r.element)) {
          seen.set(r.element, full);
          acc.push({
            id: full,
            name:
              (r.element as any)?.type?.name ??
              full.split('/').filter(Boolean).pop() ??
              full,
            path: full,
            isOk: null,
          });
        }
      }

      if (r.children?.length) traverse(r.children, full, seen, acc);
    });
    return acc;
  };

  return useMemo(
    () => traverse(pagesRoutes as any[], '', new Map(), []),
    [],
  );
};

/* ------------------------------------------------------------------ */
/*  useStatusTable – columns + tableSettings                          */
/* ------------------------------------------------------------------ */
const useStatusTable = (): [GridColDef[], tableSettingsProps] => {
  const rows = usePageStatuses(useRoutePages());

  // 🟢 only the column section changed
  const columns: GridColDef[] = useMemo(
    () => [
      { field: 'name', headerName: 'Page', flex: 1, minWidth: 180 },
      { field: 'path', headerName: 'Path', flex: 1 },
      {
        field: 'isOk',
        headerName: 'Status',
        type: 'boolean',
        flex: 0.4,
        minWidth: 100,
        valueGetter: (value, row) => row.isOk, // <— 1-arg safer
      },
      {
        field: 'lastChecked',
        headerName: 'Last Checked',
        flex: 0.8,
        minWidth: 180,
        valueFormatter: (value, row) =>
          value
            ? new Date(value as string).toLocaleString()
            : '',
      },
    ],
    [],
  );


  const tableSettings = useTableSettings({
    columns,
    idPath: ['id'],
    rows,
    tableName: 'StatusTable',
  });
 // tableSettings.loading = false;

  return [columns, tableSettings];
};

/* ------------------------------------------------------------------ */
/*  Page                                                              */
/* ------------------------------------------------------------------ */
export const StatusPage: React.FC = () => {
  const [columns, tableSettings] = useStatusTable();

  return (
    <>
      <PageHeader title='Status' />
      <ContentCardsContainer>
        <ContentCard cardId='Status'>
          <CustomDataGrid columns={columns} tableSettings={tableSettings} />
        </ContentCard>
      </ContentCardsContainer>
      <Footer />
    </>
  );
};

export default StatusPage;